import { useCallback } from "react";
import { IMLModelResume } from "../../application/models/IMLModelResume";
import { useAxiosInstances } from "../axios-instance";

interface TrainModelPayload {
  id: string;
  name_model: string;
  createdBy: string;
  modelType: "random-forest" | "support-vector";
  good_data: Record<string, any>[];
  bad_data: Record<string, any>[];
  express: boolean;
  seed: number;
}

export const useDataScienceApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();

  const getModelsListApi = useCallback(
    async (
      page: number = 1,
      pageSize: number = 10,
      userId: string,
      active?: boolean
    ): Promise<{ data: IMLModelResume[]; total: number }> => {
      try {
        const resp = await axiosAuthInstance.get("/routes/lurik-ai/models", {
          params: { page, pageSize, userId, active },
        });
        const data = resp.data.result.model || resp.data.result?.data || [];

        const formattedData = data.map((model: any) => ({
          id: model.id,
          name: model.name,
          createdBy: model.createdBy,
          createdAt: new Date(model.createdAt),
          modelType: model.modelType,
          active: model.active ?? true,
          createdByName: model.createdByName || "Desconhecido",
          status: model.status,
          progress: model.progress ?? 0,
        }));

        return { data: formattedData, total: resp.data.result?.total ?? resp.data.total ?? 0 };
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao buscar lista de modelos.");
      }
    },
    [axiosAuthInstance]
  );

  const getAllModelsApi = useCallback(
    async (userId: string): Promise<IMLModelResume[]> => {
      try {
        const resp = await axiosAuthInstance.get("/routes/lurik-ai/models", { params: { page: 1, pageSize: 9999, userId } });
        const data = resp.data.data || resp.data.result?.data || [];

        return data.map((model: any) => ({
          id: model.id,
          name: model.name,
          createdBy: model.createdBy,
          createdAt: new Date(model.createdAt),
          modelType: model.modelType,
          active: model.active ?? true,
          createdByName: model.createdByName || "Desconhecido",
          status: model.status,
          progress: model.progress ?? 0,
        }));
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao buscar modelos.");
      }
    },
    [axiosAuthInstance]
  );

  const renameModelApi = useCallback(
    async (modelId: string, newName: string, userId: string): Promise<{ id: string; name: string }> => {
      try {
        const resp = await axiosAuthInstance.put("/routes/lurik-ai/model/rename/", {modelId: modelId, name: newName, updatedBy: userId });
        return resp.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao renomear modelo.");
      }
    },
    [axiosAuthInstance]
  );

  const updateModelStatusApi = useCallback(
    async ({ modelId, status }: { modelId: string; status: boolean }) => {
      try {
        const resp = await axiosAuthInstance.put(`/routes/lurik-ai/model/status`, { id: modelId,active: status });
        return resp.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao atualizar status do modelo.");
      }
    },
    [axiosAuthInstance]
  );

  const createModelApi = useCallback(
    async (name: string, userId: string, modelType: string, companyId: string): Promise<IMLModelResume> => {
      const good_data = [
        { cnpj: "51656026000123", cnae: "41", macroregiao: "Sudeste", faturamento_anual: 999899677 },
        { cnpj: "22316437000171", cnae: "13", macroregiao: "Sudeste", faturamento_anual: 38287200 },
        { cnpj: "45157817000106", cnae: "11", macroregiao: "Sudeste", faturamento_anual: 992006452 },
        { cnpj: "24715386000186", cnae: "6201", macroregiao: "Sul", faturamento_anual: 5000000 },
        { cnpj: "29227711000185", cnae: "6201", macroregiao: "Sul", faturamento_anual: 5200000 },
        { cnpj: "34166803000132", cnae: "6201", macroregiao: "Sul", faturamento_anual: 5400000 },
        { cnpj: "06315324000103", cnae: "6201", macroregiao: "Sul", faturamento_anual: 5600000 },
        { cnpj: "47720030000136", cnae: "6201", macroregiao: "Sul", faturamento_anual: 5800000 },
        { cnpj: "17946777000146", cnae: "6201", macroregiao: "Sul", faturamento_anual: 6000000 },
        { cnpj: "34786016000193", cnae: "6201", macroregiao: "Sul", faturamento_anual: 6200000 },
      ];

      const bad_data = [
        { cnpj: "23633603000126", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1000000 },
        { cnpj: "02987794000154", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1100000 },
        { cnpj: "42078935000130", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1200000 },
        { cnpj: "35728455000102", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1300000 },
        { cnpj: "08958193000108", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1400000 },
        { cnpj: "52980474000140", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1500000 },
        { cnpj: "39429652000181", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1600000 },
        { cnpj: "08685242000178", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1700000 },
        { cnpj: "17738681000192", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1800000 },
        { cnpj: "10171903000199", cnae: "4711", macroregiao: "Sudeste", faturamento_anual: 1900000 },
      ];

      const payload = { id: "", name, createdBy: userId, companyId, modelType, good_data, bad_data, express: true, seed: 42 };

      try {
        const resp = await axiosAuthInstance.post("/routes/lurik-ai/models", payload);
        const { result } = resp.data;

        if (!result || !result.models_ias?.id) {
          throw new Error("ID do modelo não encontrado na resposta da API.");
        }

        const { models_ias, modelsML } = result;

        return {
          id: models_ias.id,
          modelsML_id: modelsML?._id || null,
          name: models_ias.name || modelsML?.model_name || "Nome não disponível",
          createdBy: models_ias.createdBy || userId,
          createdAt: models_ias.createdAt || modelsML?.createdAt || new Date(),
          modelType: models_ias.modelType || modelType,
          active: models_ias.active ?? true,
          companyId: models_ias.companyId || models_ias.company_id || "Sem empresa",
          userName: models_ias.userId || userId,
          createdByName: models_ias.createdByName || "Desconhecido",
          status: "Não Treinado",
        };
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao criar modelo.");
      }
    },
    [axiosAuthInstance]
  );

  const setModelPendingBeforeTrainingApi = useCallback(
    async (modelId: string) => {
      try {
        const resp = await axiosAuthInstance.put(`/routes/lurik-ai/models/status/pending`, { modelId: modelId, status: "pending" });
        return resp.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao atualizar status para pending.");
      }
    },
    [axiosAuthInstance]
  );

  const trainModelApi = useCallback(
    async (payload: TrainModelPayload): Promise<void> => {
      try {
        await axiosAuthInstance.post("/routes/lurik-ai/train", payload);
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao treinar modelo.");
      }
    },
    [axiosAuthInstance]
  );

  const getModelProgressApi = useCallback(
    async (id: string): Promise<{ progress: number; status: string }> => {
      try {
        const resp = await axiosAuthInstance.get(`/routes/lurik-ai/models/${id}/progress`);
        const { progress, status } = resp.data.result || { progress: 0, status: "not-trained" };
        return { progress, status };
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao buscar progresso do modelo.");
      }
    },
    [axiosAuthInstance]
  );

  const getModelDetailsApi = useCallback(
    async (modelId: string): Promise<any> => {
      try {
        const resp = await axiosAuthInstance.get(`/routes/lurik-ai/models/${modelId}`);
        return resp.data.data || resp.data.result || resp.data;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || "Erro ao buscar detalhes do modelo.");
      }
    },
    [axiosAuthInstance]
  );

  return {
    getAllModelsApi,
    getModelsListApi,
    renameModelApi,
    createModelApi,
    updateModelStatusApi,
    setModelPendingBeforeTrainingApi,
    getModelProgressApi,
    trainModelApi,
    getModelDetailsApi,
  };
};